'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  AuthState,
  clearStoredAuth,
  setStoredAuth,
} from '@/utils/auth';
import {
  setGlobalLogout,
  setAuthHeaders,
} from '@/utils/httpClient';
import useAuth0Client from '@/hooks/useAuth0Client';
import {
  useGetLoggedInUserInfo,
} from '@/hooks/useApi';
import {
  auth0Audience,
  auth0Domain,
  auth0ClientId,
} from '@/env';

type AuthContextType = AuthState & {
  logout: () => Promise<void>;
  login: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // const { disconnect: disconnectSignalR } = useContext(SignalRHubContext);
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const { client, isLoading: clientLoading, error: clientError } = useAuth0Client();
  const { mutateAsync: fetchLoggedInUserInfo } = useGetLoggedInUserInfo();

  // Initialize Auth0 SPA client, handle callback, and load user/token
  useEffect(() => {
    if (clientLoading || !client) return;
    let isMounted = true;
    (async () => {
      try {
        // Handle redirect callback if present
        const search = typeof window !== 'undefined' ? window.location.search : '';
        if (search && /[?&]code=/.test(search) && /[?&]state=/.test(search)) {
          await client.handleRedirectCallback();
          const url = new URL(window.location.href);
          url.search = '';
          window.history.replaceState({}, document.title, url.toString());
        }

        const isAuth = await client.isAuthenticated();

        if (isAuth && isMounted) {
          // Get access token and user
          const token = await client.getTokenSilently({
            authorizationParams: { audience: auth0Audience, scope: 'openid profile email' },
          });
          if (token) {
            setStoredAuth(token);
            setAuthHeaders(token);
          }
          let loggedInUserInfo = null;
          try {
            loggedInUserInfo = await fetchLoggedInUserInfo({});
          } catch (e) {
            console.warn('Failed to load logged-in user info', e);
          }

          setState({
            user: loggedInUserInfo,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Auth init error:', error);
        if (isMounted){
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Authentication failed',
          });
        }
      }
    })();
    return () => { isMounted = false; };
  }, [client, clientLoading]);

  useEffect(() => {
    if (clientError) {
      setState(prev => ({ ...prev, isLoading: false, error: clientError }));
    }
  }, [clientError]);

  const logout = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      // Disconnect SignalR connection
      // await disconnectSignalR();
      // console.log('SignalR disconnected during logout');

      // Clear stored auth data
      clearStoredAuth();
      // Send user back to the site root after logout. Ensure this origin is in Auth0 Allowed Logout URLs
      if (client) {
        await client.logout({ logoutParams: { returnTo: window.location.origin } });
      } else {
        window.location.href = window.location.origin;
      }
    } catch (error) {
      console.error('Logout error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to logout',
      }));
    }
  };

  const login = async () => {
    if (!client) return;
    try {
      await client.loginWithRedirect({
        authorizationParams: {
          audience: auth0Audience,
          scope: 'openid profile email offline_access',
        },
        appState: { returnTo: '/' },
      });
    } catch (e: any) {
      // Fallback: manual redirect to Auth0 authorize if SDK rejects (e.g., login_required bubbling up)
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: auth0ClientId,
        redirect_uri: `${window.location.origin}/authorize`,
        scope: 'openid profile email offline_access',
        audience: auth0Audience,
      });
      window.location.assign(`https://${auth0Domain}/authorize?${params.toString()}`);
    }
  };

  // Register logout function with HTTP client for global 401 handling
  useEffect(() => {
    setGlobalLogout(() => {
      console.log('HTTP Client: Global logout triggered by 401 error');
      logout();
    });
  }, [logout]);

  const value: AuthContextType = {
    ...state,
    logout,
    login,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Logout Button Component
export const LogoutButton: React.FC<{ className?: string; children?: ReactNode }> = ({
  className = '',
  children = 'Logout',
}) => {
  const { logout, isLoading } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <button
      onClick={handleLogout}
      disabled={isLoading}
      className={`px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 ${className}`}
    >
      {isLoading ? 'Logging out...' : children}
    </button>
  );
};